<template>
  <CardBase v-if="table">
    <Teleport to="body">
      <ProjectDetails
        v-if="showDetails && selectedProject && selectedBadgeColor"
        :project="selectedProject"
        :internal="filters.submitted.type === FeatureType.ProjectAnalysisInternal"
        :selectedBadgeColor="selectedBadgeColor"
        @projectDetailsClose="() => (showDetails = false)"
      />
    </Teleport>
    <TableBase
      :table="table"
      :bodyHeight="isFeatureFullscreen ? 'auto' : '300px'"
      :sort-by="(i) => statusOrder(table.body[i][2].cell.status)"
      layout="fixed"
      rightTextAlign="center"
      :tabbableRows="true"
      caption="Projekt table"
      @mouseEnterCell="hoveredIndex = $event.cell.index"
      @mouseLeaveCell="hoveredIndex = null"
      @onClickRow="onClickRow"
    >
      <template #header-cell="{ cell: { label } }">
        <TextBase :text="label?.title" textWrap="nowrap" :type="'paragraph'" :weight="'bold'" />
      </template>
      <template #body-cell="{ cell, cellIndex }">
        <div>
          <TextBase
            v-if="cellIndex % 3 === 0"
            :text="cell.cell.label"
            textWrap="nowrap"
            :type="'paragraph'"
            :weight="'regular'"
            :overflow="'ellipsis'"
            overflow-tooltip
          />
        </div>
        <BadgeSquare
          v-if="cellIndex % 3 === 1"
          :badgeText="cell.cell.label"
          :size="'small'"
          :color="'primary'"
          :style="{ textAlign: 'left' }"
        />
        <div v-if="cellIndex % 3 === 2" class="button-row">
          <BadgeSquare
            :badgeText="cell.cell.label"
            :size="'small'"
            :containerStyle="{ 'background-color': cell.cell.badgeColor }"
          />
          <SvgDisplay :src="'Icon-Arrow-Up-Right_x16'" />
        </div>
      </template>
    </TableBase>
  </CardBase>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'
import TableBase from '@/design-system/atoms/TableBase.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import SvgDisplay from '@/design-system/atoms/SvgDisplay.vue'
import useFetchProjects, { ProjectQuery } from '@/api-client/models/project/useFetchProjects'
import { PROJECT_STATUS, PROJECT_TYPE, BADGE_COLORS } from '@/api-client/models/project/Constants'
import CardBase from '@/components/AnalyticsDashboard/components/CardBase.vue'
import ProjectDetails from '@/components/AnalyticsDashboard/components/ProjectDetails.vue'
import { Table } from '@/design-system/atoms/table-base'
import { FeatureType, useCurrentFeatureFilters, useFilters } from '@/composables/useFilters'
import { PROJECT_STATUSES, Project, ProjectStatus } from '@/api-client/models/project/Contracts'
import { calculateEntityIds, calculateEntityType } from '@/fetches'

type HeaderCell = {
  label: {
    title: string
    subtitle?: string
  }
}

type BodyCell = {
  cell: {
    index: number
    label: string
    id?: string
    status?: ProjectStatus
    badgeColor?: string
    backgroundColor?: string
    project: Project
  }
}

type ProjectTable = Table<HeaderCell, BodyCell>

const piniaStore = useGeneralStore()

const hoveredIndex = ref<number | null>(null)
const showDetails = ref(false)
const selectedProject = ref<Project | undefined>(undefined)
const selectedBadgeColor = ref<string | undefined>(undefined)
const { locale } = useI18n()
const query = ref<ProjectQuery>()

const { data } = useFetchProjects(query)

const currentFeature = useCurrentFeatureFilters()
const filterType = computed(
  (): FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal => {
    if (!currentFeature.value) {
      return FeatureType.ProjectAnalysisExternal
    }
    return currentFeature.value.submitted.type as
      | FeatureType.ProjectAnalysisExternal
      | FeatureType.ProjectAnalysisInternal
  }
)
const isFeatureFullscreen = computed(() => {
  return piniaStore.isFeatureFullscreen
})
const filters = useFilters<
  FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal
>(filterType.value)

watch(
  () => filters.submitted,
  (value) => {
    if (!value) {
      query.value = {
        page: 0,
        pageSize: 100,
        internal: filters.submitted?.type === FeatureType.ProjectAnalysisInternal
      }
      return
    }

    const internal = value.type === FeatureType.ProjectAnalysisInternal
    const entityType = calculateEntityType(value)
    const entityIds = calculateEntityIds(value)

    const budgetFrom = value.budget?.from ? value.budget.from * 1000 : undefined
    const budgetTo = value.budget?.to ? value.budget.to * 1000 : undefined

    query.value = {
      page: 0,
      pageSize: 100,
      internal,
      budgetFrom,
      budgetTo,
      entityType,
      entityIds,
      statuses: value.projectStatuses,
      types: value.projectTypes
    }
  },
  { immediate: true }
)

const filteredItems = computed(() => {
  return data.value?.items ?? []
})

const table = computed<ProjectTable>((): ProjectTable => {
  const headers: ProjectTable['headers'] = {
    top: [
      {
        label: {
          title: `${filteredItems.value.length} Projekt`
        },
        style: {
          width: '40%',
          height: '32px',
          padding: '4px',
          'background-color': '#D5D7F666'
        }
      },
      {
        label: {
          title: 'Typ'
        },
        style: {
          width: '35%',
          height: '32px',
          padding: '4px',
          'text-align': 'left',
          'background-color': '#D5D7F666'
        }
      },
      {
        label: {
          title: 'Status'
        },
        style: {
          width: '25%',
          height: '32px',
          padding: '4px',
          'text-align': 'left',
          'background-color': '#D5D7F666'
        }
      }
    ]
  }
  const body: ProjectTable['body'] = filteredItems.value.map((project, index) => {
    return [
      {
        cell: {
          index: index,
          label: project.name
        },
        style: {
          width: '40%',
          padding: '4px',
          backgroundColor: hoveredIndex.value === index ? '#CDDEFE33' : 'transparent',
          cursor: 'pointer'
        }
      },
      {
        cell: {
          index: index,
          label: PROJECT_TYPE[project.type][locale.value]
        },
        style: {
          width: '35%',
          padding: '4px',
          backgroundColor: hoveredIndex.value === index ? '#CDDEFE33' : 'transparent',
          cursor: 'pointer'
        }
      },
      {
        cell: {
          index: index,
          id: project.id,
          label: PROJECT_STATUS[project.status][locale.value],
          status: project.status,
          badgeColor: BADGE_COLORS[project.status],
          project: project
        },
        style: {
          width: '25%',
          padding: '4px',
          backgroundColor: hoveredIndex.value === index ? '#CDDEFE33' : 'transparent',
          cursor: 'pointer'
        }
      }
    ]
  })
  return {
    headers,
    body
  }
})

function onClickRow(row?: BodyCell[]) {
  if (!row || !row?.[2].cell.project || !row?.[2].cell.badgeColor) {
    return
  }
  selectedBadgeColor.value = row?.[2].cell.badgeColor
  selectedProject.value = row?.[2].cell.project
  showDetails.value = true
}

function statusOrder(status?: ProjectStatus) {
  return status ? PROJECT_STATUSES.indexOf(status) : 0
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';

button {
  all: unset;
  padding: 4px;
  &:hover {
    border-radius: 4px;
    background-color: aliceblue;
  }
}
.button-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

:deep(*) {
  #compensator {
    background-color: #d5d7f666;
  }
}
</style>
